import * as React from 'react';

import {exhaustivenessCheck} from 'nutshell-core/typing/exhaustiveness-check';

import {colors} from 'shells/colors/colors';
import {TextRegularAiIcon} from 'shells/icon/svg/text-regular-ai-icon';
import {Stack} from 'shells/layout/stack/stack';
import {LeadSummaryPopover} from 'shells/summary-popover/lead/lead-summary-popover';
import {SummaryPopover} from 'shells/summary-popover/summary-popover';
import {Body} from 'shells/typography/body';

import type {EntitySummaryRating} from 'nutshell-graphql-types';
import type {
    EntitySummaryFragment_EntitySummary as EntitySummary,
    EntitySummaryFragment_LeadEntitySummary as LeadEntitySummary,
} from 'nutshell-graphql-types';

import {useLeadRecapSurvey} from '../../internal-feedback/hooks/use-lead-recap-survey';
import {OpenableModals} from '../../openable-modals/openable-modals';
import {useOpenableModal} from '../../openable-modals/use-openable-modal';
import {useRateEntitySummary} from '../../power-ai/timeline-summary/graphql/hooks/use-rate-entity-summary';

type Props = {
    summary: EntitySummary | LeadEntitySummary | null | undefined;
    title: string;
    isGenerating?: boolean;
    entityId?: string;
    onRefresh?: () => Promise<any>;
};

// TODO: This should probably be moved to shells
export function AiSummaryTruncated(props: Props) {
    const anchorRef = React.useRef(null);
    const [isHovered, setIsHovered] = React.useState(false);

    const {rateEntitySummary, isSavingRating} = useRateEntitySummary();
    const {onOpen: openLearnMoreModal} = useOpenableModal(OpenableModals.HELP_LEAD_RECAPS_MODAL);

    const rateLeadSummary = React.useCallback(
        (rating: EntitySummaryRating) => {
            if (!props.summary) {
                return Promise.resolve();
            }

            return rateEntitySummary({entitySummaryId: props.summary.id, rating});
        },
        [props.summary, rateEntitySummary]
    );

    const {
        handleRate: handleLeadRate,
        openSurvey,
        SurveyModal,
    } = useLeadRecapSurvey({
        onRate: rateLeadSummary,
        entitySummaryId: props.summary?.id,
    });

    const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleMouseEnter = () => {
        hoverTimerRef.current = setTimeout(() => {
            setIsHovered(true);
        }, 700);
    };

    const handleMouseLeave = () => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
        setIsHovered(false);
    };

    if (props.isGenerating) {
        return (
            <>
                <Stack isHorizontal={true} spacing={4} alignItems='center'>
                    <TextRegularAiIcon
                        // @ts-expect-error
                        color='ai-purple'
                        fill='ai-purple'
                        size={20}
                        alignCenter={true}
                        wrapWithDiv={false}
                    />
                    <Body color='light' size='small'>
                        Generating summary…
                    </Body>
                </Stack>
                {SurveyModal}
            </>
        );
    }

    type TextAndPopover = {
        text: string;
        popover: React.ReactNode;
    };

    let textAndPopover: TextAndPopover | null | undefined = null;
    if (props.summary != null) {
        const summary = props.summary;
        const handleRate = (rating: EntitySummaryRating): Promise<any> => {
            return rateEntitySummary({entitySummaryId: summary.id, rating});
        };

        switch (props.summary.__typename) {
            case 'LeadEntitySummary':
                if (props.summary.recap != null && props.summary.nextSteps != null) {
                    textAndPopover = {
                        text: props.summary.recap,
                        popover: (
                            <LeadSummaryPopover
                                anchor={anchorRef.current}
                                anchorElementId='ai-summary-popover'
                                recap={props.summary.recap}
                                nextSteps={props.summary.nextSteps}
                                entityId={props.entityId}
                                userRating={props.summary.userRating}
                                onRate={handleLeadRate}
                                isSavingRating={isSavingRating}
                                onRefresh={props.onRefresh}
                                onFeedback={openSurvey}
                                onLearnMore={openLearnMoreModal}
                            />
                        ),
                    };
                }
                break;
            case 'EntitySummary':
                if (props.summary.summary != null) {
                    textAndPopover = {
                        text: props.summary.summary,
                        popover: (
                            <SummaryPopover
                                anchor={anchorRef.current}
                                anchorElementId='ai-summary-popover'
                                title={props.title}
                                summary={props.summary.summary}
                                userRating={props.summary.userRating}
                                onRate={handleRate}
                                isSavingRating={isSavingRating}
                            />
                        ),
                    };
                }
                break;
            default:
                exhaustivenessCheck(props.summary);
        }
    }

    if (textAndPopover == null) {
        return (
            <>
                <Body color='light' size='small'>
                    No summary available
                </Body>
                {SurveyModal}
            </>
        );
    }

    return (
        <>
            <div
                ref={anchorRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className='gap-4 flex'
            >
                <div className='flex-shrink'>
                    <TextRegularAiIcon
                        // @ts-expect-error
                        color={colors.aiPurplePrimary}
                        fill={colors.aiPurplePrimary}
                        size={20}
                        alignCenter={true}
                        wrapWithDiv={false}
                    />
                </div>
                <Body truncate={{type: 'block', maxLines: 2}} size='small'>
                    {textAndPopover.text}
                </Body>
                {isHovered && (
                    <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                        {textAndPopover.popover}
                    </div>
                )}
            </div>
            {SurveyModal}
        </>
    );
}
